import {
  type ChatAskRequest,
  type ChatInfoResponse,
  type ChatSseEvent,
  type ChatSuggestionsResponse,
} from '@aura/shared';
import { SseParser } from './sse.js';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const networkError = () =>
  new ApiError(
    'NETWORK',
    'No se pudo conectar con el asistente. Revisá tu conexión y probá de nuevo.',
  );

/**
 * Cómo se autentica cada pedido: la sesión del usuario en su sistema (`TokenAuth`) o la clave
 * compartida (`AccessKeyAuth`, con `data-access-key`). El resto del widget no cambia.
 */
export interface WidgetAuth {
  headers(): Promise<Record<string, string>>;
  /** Si existe, la API respondió 401: renovar la credencial antes del único reintento. */
  refresh?(): Promise<void>;
}

const USER_STORAGE = 'aura.userId';

/** Id anónimo y estable de este navegador (no autentica: agrupa las preguntas por usuario). */
function anonymousUserId(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem(USER_STORAGE);
  } catch {
    /* sin almacenamiento: un id nuevo por carga */
  }
  if (!id || !/^[\w-]{8,64}$/.test(id)) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    try {
      localStorage.setItem(USER_STORAGE, id);
    } catch {
      /* idem */
    }
  }
  return id;
}

export class AccessKeyAuth implements WidgetAuth {
  private readonly userId = anonymousUserId();

  constructor(private key?: string) {}

  /** En la página de chat, la clave la escribe el usuario (y puede cambiarla si era incorrecta). */
  setKey(key: string | undefined): void {
    this.key = key;
  }

  get hasKey(): boolean {
    return Boolean(this.key);
  }

  headers(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'x-aura-user': this.userId };
    if (this.key) headers['x-access-key'] = this.key;
    return Promise.resolve(headers);
  }
}

type ErrorBody = { error?: { code?: string; message?: string } };

async function errorFrom(res: Response): Promise<ApiError> {
  let body: ErrorBody | undefined;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    /* respuesta sin JSON */
  }
  return new ApiError(
    body?.error?.code ?? `HTTP_${res.status}`,
    body?.error?.message ?? 'Algo salió mal. Probá de nuevo.',
  );
}

export class ChatApi {
  constructor(
    private readonly baseUrl: string,
    private readonly auth: WidgetAuth,
  ) {}

  async info(): Promise<ChatInfoResponse> {
    const res = await this.fetch('/v1/chat/info', { method: 'GET' });
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as ChatInfoResponse;
  }

  /** Preguntas sugeridas para el chat vacío. Son un extra: si fallan, no hay sugerencias. */
  async suggestions(): Promise<string[]> {
    let res: Response;
    try {
      res = await this.fetch('/v1/chat/suggestions', { method: 'GET' });
    } catch {
      return [];
    }
    // Una clave rechazada sí se informa: la página de chat la usa para validar la clave.
    if (res.status === 401) {
      const error = await errorFrom(res);
      if (error.code === 'ACCESS_KEY_INVALID') throw error;
    }
    if (!res.ok) return [];
    try {
      return ((await res.json()) as ChatSuggestionsResponse).questions;
    } catch {
      return [];
    }
  }

  /**
   * Pregunta en SSE: llama a `onEvent` por cada evento. Un evento `error` se lanza como
   * `ApiError`; si la conexión se corta antes de `done`, también.
   */
  async ask(
    body: ChatAskRequest,
    onEvent: (e: ChatSseEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const res = await this.fetch('/v1/chat/ask/stream', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) throw await errorFrom(res);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser();
    let done = false;
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (err) {
        if (signal?.aborted) throw err;
        throw networkError();
      }
      if (chunk.done) break;
      for (const e of parser.push(decoder.decode(chunk.value, { stream: true }))) {
        if (e.event === 'error') throw new ApiError(e.data.code, e.data.message);
        if (e.event === 'done') done = true;
        onEvent(e);
      }
    }
    if (!done) throw networkError();
  }

  /** Un 401 de la API (token vencido o revocado) renueva la credencial y reintenta una vez. */
  private async fetch(path: string, init: RequestInit, retried = false): Promise<Response> {
    const headers = { ...(init.headers as Record<string, string>), ...(await this.auth.headers()) };
    let res: Response;
    try {
      // Sin cookies: el widget corre en el dominio del sistema y la API está en otro.
      res = await fetch(this.baseUrl + path, { ...init, headers, credentials: 'omit' });
    } catch (err) {
      if (init.signal?.aborted) throw err;
      throw networkError();
    }
    if (res.status === 401 && this.auth.refresh && !retried) {
      await this.auth.refresh();
      return this.fetch(path, init, true);
    }
    return res;
  }
}
